import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, User, Mail, Phone } from 'lucide-react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useCart } from '@/contexts/CartContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GuestCheckoutPage() {
  const navigate = useNavigate();
  const { cart, cartTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    notes: ''
  });

  if (!cart.items?.length) {
    navigate('/cart');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const orderData = {
        ...formData,
        items: cart.items
      };

      const response = await axios.post(`${API}/orders/guest`, orderData);
      
      toast.success('Ordine creato con successo!');
      clearCart();
      
      // Mostra riepilogo ordine
      navigate('/', { 
        state: { 
          orderConfirmed: true, 
          orderId: response.data.id,
          guestEmail: formData.guest_email 
        } 
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la creazione dell\'ordine');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8">
        <div className="mb-8 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
          <h2 className="text-lg font-bold text-emerald-900 mb-2">
            ✓ Ordine Ospite - Ritiro in Negozio
          </h2>
          <p className="text-sm text-emerald-700">
            Stai effettuando un ordine come ospite. Ritirerai e pagherai direttamente in negozio.
          </p>
        </div>

        <h1 className="text-4xl font-extrabold mb-8" data-testid="guest-checkout-title">
          Completa il Tuo Ordine
        </h1>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="bg-white p-6 rounded-2xl border border-gray-200">
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <User className="h-6 w-6 text-rose-600" />
                  I Tuoi Dati
                </h2>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="guest_name">Nome Identificativo Ordine *</Label>
                    <Input
                      id="guest_name"
                      value={formData.guest_name}
                      onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })}
                      placeholder="Es: Mario Rossi"
                      required
                      data-testid="guest-name-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Questo nome verrà usato per identificare il tuo ordine in negozio
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="guest_email">Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="guest_email"
                        type="email"
                        value={formData.guest_email}
                        onChange={(e) => setFormData({ ...formData, guest_email: e.target.value })}
                        placeholder="tua@email.com"
                        className="pl-10"
                        required
                        data-testid="guest-email-input"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Riceverai conferma dell'ordine via email
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="guest_phone">Numero di Telefono *</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="guest_phone"
                        type="tel"
                        value={formData.guest_phone}
                        onChange={(e) => setFormData({ ...formData, guest_phone: e.target.value })}
                        placeholder="+39 123 456 7890"
                        className="pl-10"
                        required
                        data-testid="guest-phone-input"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Ti contatteremo quando l'ordine sarà pronto
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="notes">Note Aggiuntive (Opzionale)</Label>
                    <Textarea
                      id="notes"
                      placeholder="Inserisci eventuali note per il tuo ordine..."
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={3}
                      data-testid="guest-notes"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-emerald-50 p-6 rounded-2xl border border-emerald-200">
                <h3 className="font-bold text-emerald-900 mb-3">Modalità di Ritiro e Pagamento:</h3>
                <ul className="space-y-2 text-sm text-emerald-800">
                  <li className="flex items-start">
                    <span className="mr-2">✓</span>
                    <span><strong>Ritiro:</strong> In negozio durante gli orari di apertura</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">✓</span>
                    <span><strong>Pagamento:</strong> In negozio al momento del ritiro (contanti o carta)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">✓</span>
                    <span><strong>Notifica:</strong> Ti contatteremo quando l'ordine sarà pronto</span>
                  </li>
                </ul>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-rose-600 hover:bg-rose-700 text-white rounded-full font-bold py-6"
                data-testid="confirm-guest-order"
              >
                {loading ? 'Elaborazione...' : 'Conferma Ordine'}
              </Button>
            </form>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-orange-50 p-6 rounded-2xl border border-gray-200 sticky top-24">
              <h2 className="text-2xl font-bold mb-4">Riepilogo Ordine</h2>

              <div className="space-y-3 mb-4">
                {cart.items.map((item) => (
                  <div key={item.product_id} className="flex justify-between text-sm border-b pb-2">
                    <span className="text-gray-600">
                      {item.name} x {item.quantity}
                    </span>
                    <span className="font-semibold">€{(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="flex justify-between text-xl font-bold pt-4 border-t">
                <span>Totale</span>
                <span className="text-emerald-600" data-testid="guest-total">€{cartTotal.toFixed(2)}</span>
              </div>

              <p className="text-xs text-gray-500 mt-4 text-center">
                Pagamento al ritiro in negozio
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
